/* eslint-disable no-alert */
/* eslint-disable no-unused-expressions */
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AddIcon, EditIcon, ExpandIcon } from 'src/v3/core/icons'

import * as yup from 'yup'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectActiveLinkedService,
  fetchTemplates,
  selectTemplatesStatus,
  selectTemplatesByLinkedServiceID,
  fetchCourses,
  selectCoursesStatus,
  selectCourses,
  fetchLinkedServices,
  selectLinkedServicesByIds,
  selectLinkedServicesStatus,
} from 'src/v3/create/create.slice'

import { useFormik } from 'formik'
import axios from 'src/utilities/http-client'
import { compose, F, T } from 'ramda'
import useSession from 'src/authentication/hooks/use-session'
import ModernLinkedServiceSelector from './components/modern-linked-service-selector'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'

export default (): ReactElement => {
  let session = useSession()
  let dispatch = useAppDispatch()

  let activeLinkedService = useAppSelector(selectActiveLinkedService)
  let linkedServices = useAppSelector(
    selectLinkedServicesByIds(session?.linkedServices ?? []),
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let templates = useAppSelector(
    selectTemplatesByLinkedServiceID(activeLinkedService),
  )
  let templatesStatus = useAppSelector(selectTemplatesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)

  let [errorMessage, setErrorMessage] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [deleting, setDeleting] = useState<string>('')
  let [formOpen, setFormOpen] = useState<boolean>(false)

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      statement1: yup.string().required(),
      statement2: yup.string().required(),
      linkedService: yup.string().not(['unselected']).required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      statement1: '',
      statement2: '',
      linkedService: 'unselected',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/template', {
            name: values.name,
            statement1: values.statement1,
            statement2: values.statement2,
            linkedServiceId:
              values.linkedService === 'FOAMfrat' ? null : values.linkedService,
          })
          .then(() => dispatch(fetchTemplates()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Template')
            dispatch(fetchTemplates())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/template', {
            id: values.id,
            name: values.name,
            statement1: values.statement1,
            statement2: values.statement2,
          })
          .then(() => dispatch(fetchTemplates()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Template')
            dispatch(fetchTemplates())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  useEffect(() => {
    if (linkedServicesStatus === 'uninitialized') {
      dispatch(fetchLinkedServices())
    }
  }, [linkedServicesStatus])

  useEffect(() => {
    if (templatesStatus === 'uninitialized') {
      dispatch(fetchTemplates())
    }
  }, [templatesStatus])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (!formOpen) {
      form.resetForm()
    }
  }, [formOpen])

  let onEdit = (id: string) => {
    let template = templates.find((t) => t.id === id)
    form.setFieldValue('id', template?.id)
    form.setFieldValue('name', template?.name)
    form.setFieldValue('statement1', template?.statement1)
    form.setFieldValue('statement2', template?.statement2)
    form.setFieldValue('linkedService', template?.linkedServiceID)
    setFormOpen(true)
  }

  return (
    <>
      <Container
        maxWidth="xl"
        sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Templates
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Create a template or select one to edit
          </Typography>
        </Box>
        <Box className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Box sx={{ mb: 3 }}>
            <ModernLinkedServiceSelector />
          </Box>
        </Box>
        <Box className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <Card variant="outlined" sx={{ height: 'fit-content' }}>
            <CardContent>
              <Box className="space-y-4">
                <Box className="space-y-2">
                  <Typography variant="h6" component="div">
                    <Skeleton animation={false} />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <Skeleton animation={false} />
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <Skeleton animation={false} />
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={compose(setFormOpen, T)}
                  >
                    Add New Template
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
          {templates.map((t, i) => (
            <Card
              key={`${t.name}-${i}`}
              variant="outlined"
              sx={{ height: '100%' }}
            >
              <CardContent>
                <Box className="space-y-4">
                  <Box>
                    <Typography variant="h6" component="div" sx={{ mb: 2 }}>
                      {t.name}
                    </Typography>
                    <Box className="space-y-3">
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Accrediting Agency 1 Statement
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t.statement1 || 'None'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Accrediting Agency 2 Statement
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t.statement2 || 'None'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
                  >
                    <Tooltip title="Edit">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onEdit(t.id)}
                      >
                        <EditIcon />
                      </Button>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDeleting(`delete-${t.id}`)}
                        disabled={
                          templates.length === 1 ||
                          courses.some((c) => c.templateID === t.id)
                        }
                      >
                        <DeleteForeverIcon />
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
                <ConfirmAction
                  executeAction={() =>
                    axios
                      .delete(
                        `/api/v2/create/delete-template?templateID=${t.id}`,
                      )
                      .then(() => dispatch(fetchTemplates()))
                  }
                  open={deleting}
                  setOpen={setDeleting}
                  openText={`delete-${t.id}`}
                  title="Confirm Deletion"
                  text={`Are you sure you want to delete ${t.name}?`}
                  onFailure={(err) =>
                    setErrorMessage(err.message || 'Error Deleting Template')
                  }
                />
              </CardContent>
            </Card>
          ))}
        </Box>

        <Dialog
          open={formOpen && !saving}
          onClose={compose(setFormOpen, F)}
          maxWidth="sm"
          fullWidth
        >
          <form onSubmit={form.handleSubmit}>
            <DialogTitle>
              {form.values.id ? 'Edit Template' : 'Create New Template'}
            </DialogTitle>
            <DialogContent>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
              >
                <TextField
                  fullWidth
                  value={form.values.name}
                  label="Name"
                  name="name"
                  onChange={form.handleChange}
                  error={form.touched.name && !!form.errors.name}
                />

                <TextField
                  fullWidth
                  value={form.values.statement1}
                  label="Accrediting Agency 1 Statement*"
                  name="statement1"
                  multiline
                  rows={3}
                  onChange={form.handleChange}
                  error={form.touched.statement1 && !!form.errors.statement1}
                />

                <TextField
                  fullWidth
                  value={form.values.statement2}
                  label="Accrediting Agency 2 Statement*"
                  name="statement2"
                  multiline
                  rows={3}
                  onChange={form.handleChange}
                  error={form.touched.statement2 && !!form.errors.statement2}
                />

                <FormControl fullWidth>
                  <InputLabel>Associated Linked Service</InputLabel>
                  <Select
                    value={form.values.linkedService}
                    label="Associated Linked Service"
                    onChange={(e: any) =>
                      form.setFieldValue('linkedService', e.target.value)
                    }
                    error={
                      form.touched.linkedService && !!form.errors.linkedService
                    }
                  >
                    <MenuItem key="unselected" value="unselected">
                      Unselected
                    </MenuItem>
                    {linkedServices.reduce(
                      (acc: ReactElement[], ls) => {
                        // eslint-disable-next-line fp/no-mutating-methods
                        acc.push(
                          <MenuItem key={ls.id} value={ls.id}>
                            {ls.name}
                          </MenuItem>,
                        )
                        return acc
                      },
                      session?.roles.includes('admin')
                        ? [
                            <MenuItem key="FF" value="FOAMfrat">
                              FOAMfrat
                            </MenuItem>,
                          ]
                        : [],
                    )}
                  </Select>
                </FormControl>

                <Typography variant="body2" color="textSecondary">
                  *In an accreditation statement that includes the length of the
                  course, enter "$HOUR" and the length of the course will be
                  inserted in the certificate.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={compose(setFormOpen, F)}>Cancel</Button>
              <Button variant="contained" type="submit">
                {form.values.id ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[coursesStatus, linkedServicesStatus, templatesStatus]}
        loading={saving}
      />
    </>
  )
}
